import { BlobClient } from '@azure/storage-blob';
import { Badge, Box, Button, Flex, IconButton, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { documentAction, translationAction } from 'actions';
import { requestDownloadAllTranslatedFiles } from 'actions/translation';
import configs from 'configs';
import FilePreviewDialog, { PreviewFileState } from 'containers/Summary/FilePreviewDialog';
import { HTTP_CODE_RESPONSE } from 'helpers/constant';
import {
  DOWNLOAD_TRANSLATED_FILE_STATUS,
  TRANSLATED_FILE_STATUS,
  TRANSLATION_FILE_STATUS,
} from 'helpers/enum';
import { addTokenURL, getFileExtension } from 'helpers/file';
import { CircleAlert, Download, FileText, Languages } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UploadService } from 'services/upload/upload.service';
import { TranslatedFile } from 'types/reducers/translation';
import { downloadFileBlob, getStorageReadBlobToken } from 'utils/blob';

const TranslatedFiles = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { current, downloadTranslatedFilesStatus } = useSelector((store) => store.translation);
  const { output_languages: outputLanguages, input_languages: inputLanguages } = useSelector(
    (store) => store.translation.current
  );
  const isUploading = !current.selectedFiles.every(
    (file) => file.translation_file_status === TRANSLATION_FILE_STATUS.UPDATED
  );

  const [isShowingPreviewFile, setIsShowingPreviewFile] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<PreviewFileState>({});

  const { selectedFiles: currentFileSelections, results: translatedFiles } = current;
  const isShowingSubmitButton = outputLanguages.length > 0 && currentFileSelections?.length > 0;
  const isAllFilesFinishProcessed = translatedFiles?.every((file) =>
    [TRANSLATED_FILE_STATUS.GENERATED, TRANSLATED_FILE_STATUS.ERROR].includes(file.status)
  );

  const isPendingDownloadAllFiles =
    downloadTranslatedFilesStatus === DOWNLOAD_TRANSLATED_FILE_STATUS.PENDING;

  const isAllFilesError = translatedFiles?.every((file) =>
    [TRANSLATED_FILE_STATUS.ERROR].includes(file.status)
  );

  const isPendingFileExist = translatedFiles?.some(
    (file) => file.status === TRANSLATED_FILE_STATUS.PENDING
  );

  const generateTranslatedFileList = () => {
    dispatch(translationAction.updateCurrentTranslatedFiles(null));
  };

  const onSubmit = useCallback(async () => {
    if (isPendingFileExist || isPendingDownloadAllFiles) {
      return;
    }
    if (isAllFilesFinishProcessed) {
      dispatch(requestDownloadAllTranslatedFiles());
      return;
    }

    dispatch(translationAction.requestTranslation());
  }, [current]);

  useEffect(() => {
    generateTranslatedFileList();
  }, [currentFileSelections, inputLanguages, outputLanguages]);

  const checkReadTokenValid = (token: string | null) => {
    if (!token) {
      return false;
    }
    const searchParams = new URLSearchParams(token);
    const expiredTime = new Date(searchParams.get('se') as string).getTime();
    const currentTime = new Date().getTime();
    const offsetRefreshTime = configs.setting.TIMEOUT_REFRESH_SAS_TOKEN_IN_MS;

    return expiredTime > currentTime + offsetRefreshTime;
  };

  const onOpenFile = (file: TranslatedFile) => {
    readFile(file);
  };

  const readFile = (file: TranslatedFile) => {
    if (!checkReadTokenValid(getStorageReadBlobToken())) {
      dispatch(documentAction.getReadToken());
    }
    fetchData(file);
  };

  const fetchData = (file: TranslatedFile) => {
    if (!file?.url) {
      return;
    }
    const fileUrl = addTokenURL(file?.url, getStorageReadBlobToken());
    setPreviewFile({
      url: fileUrl,
      name: file?.name,
      type: getFileExtension(file?.url),
    });

    (async () => {
      try {
        const res = await UploadService.fetchHeadFile(fileUrl);
        if (
          res.statusText !== 'OK' &&
          (res.status === HTTP_CODE_RESPONSE.UNAUTHORIZED ||
            res.status === HTTP_CODE_RESPONSE.NO_SAS_TOKEN)
        ) {
          readFile(file);
        } else {
          setIsShowingPreviewFile(true);
        }
      } catch (error) {
        console.log(error);
      }
    })();
  };

  const onClose = () => {
    setIsShowingPreviewFile(false);
  };

  const downloadFile = async (
    e: React.MouseEvent<HTMLButtonElement | SVGElement, MouseEvent>,
    file: TranslatedFile
  ) => {
    e.stopPropagation();
    if (file) {
      const { name = '', url = '' } = file;
      const container = new BlobClient(addTokenURL(url, getStorageReadBlobToken()));
      const downloadBlockBlobResponse = await container.download();
      const blobData = await downloadBlockBlobResponse.blobBody;
      if (blobData) {
        downloadFileBlob(blobData, name);
      }
    }
  };

  return (
    /* DS translated-output pane — surface, border, ds_sm shadow (design-guidelines.md §4, SC-03) */
    <Flex
      pos={'relative'}
      direction='column'
      overflow='hidden'
      h={'100%'}
      minH={0}
      bg='bg.surface'
      borderWidth='1px'
      borderColor='border.default'
      borderRadius='lg'
      shadow='ds_sm'
    >
      <FilePreviewDialog isOpen={isShowingPreviewFile} file={previewFile} onClose={onClose} />

      {/* Header: title + status badge */}
      <Flex align='center' justify='space-between' flexShrink={0} px={4} pt={4} pb={3}>
        <Text color='text.default' fontFamily='heading' fontSize='md' fontWeight={600}>
          {t('translator.translatedFiles')}
        </Text>
        {isPendingFileExist && (
          <Badge variant='ds-warning'>{t('translator.fileIsProcessing')}</Badge>
        )}
        {!isPendingFileExist && isAllFilesFinishProcessed && (translatedFiles?.length ?? 0) > 0 && (
          <Badge variant={isAllFilesError ? 'ds-error' : 'ds-success'}>
            {isAllFilesError ? t('translator.errorFile') : t('translator.fileIsSaved')}
          </Badge>
        )}
      </Flex>

      {/* Output list / empty state */}
      <Box flex={1} overflowY='auto' px={3} pb={3}>
        {!translatedFiles || translatedFiles.length === 0 ? (
          <Text px={3} py={6} color='text.muted' fontSize='sm' textAlign='center'>
            {t('translator.translatedFilesEmpty', {
              defaultValue:
                'No output yet. Pick source files and target languages, then translate.',
            })}
          </Text>
        ) : (
          <Flex direction='column' gap={2}>
            {translatedFiles.map((file) => {
              const isDone = file.status === TRANSLATED_FILE_STATUS.GENERATED;
              return (
                <Flex
                  key={file.id}
                  align='center'
                  gap={3}
                  px={3}
                  py={3}
                  bg='bg.surface'
                  borderWidth='1px'
                  borderColor={isDone ? 'primary.solid' : 'border.default'}
                  borderRadius='md'
                  _hover={{ borderColor: 'primary.solid', shadow: 'ds_xs' }}
                  cursor='pointer'
                  transition='all .14s ease-out'
                  onClick={() => onOpenFile(file)}
                >
                  <Flex
                    align='center'
                    justify='center'
                    flexShrink={0}
                    w='32px'
                    h='32px'
                    color='primary.solid'
                    bg='primary.soft'
                    borderRadius='sm'
                  >
                    <FileText size={15} />
                  </Flex>
                  <Tooltip label={file.name} placement='top-start'>
                    <Box flex={1} minW={0}>
                      <Text
                        overflow='hidden'
                        color='text.default'
                        fontSize='sm'
                        fontWeight={500}
                        whiteSpace='nowrap'
                        textOverflow='ellipsis'
                      >
                        {file.name}
                      </Text>
                    </Box>
                  </Tooltip>
                  {file.status === TRANSLATED_FILE_STATUS.PENDING && (
                    <Tooltip label={t('translator.fileIsProcessing')}>
                      <Box flexShrink={0}>
                        <Spinner color='text.subtle' size='sm' />
                      </Box>
                    </Tooltip>
                  )}
                  {file.status === TRANSLATED_FILE_STATUS.ERROR && (
                    <Tooltip label={t('translator.errorFile')}>
                      <Box flexShrink={0} color='state.error'>
                        <CircleAlert size={16} />
                      </Box>
                    </Tooltip>
                  )}
                  {isDone && (
                    <Tooltip label={t('common.download')}>
                      <IconButton
                        flexShrink={0}
                        aria-label={t('common.download')}
                        icon={<Download size={15} />}
                        onClick={(e) => downloadFile(e, file)}
                        size='ds-sm'
                        variant='ds-ghost'
                      />
                    </Tooltip>
                  )}
                </Flex>
              );
            })}
          </Flex>
        )}
      </Box>

      {/* Footer action */}
      {!isPendingFileExist && !isAllFilesError && isShowingSubmitButton && (
        <Box flexShrink={0} px={4} py={4} borderColor='border.default' borderTopWidth='1px'>
          <Button
            w='100%'
            isDisabled={isUploading || isPendingDownloadAllFiles}
            leftIcon={isAllFilesFinishProcessed ? <Download size={17} /> : <Languages size={17} />}
            onClick={onSubmit}
            size='ds-md'
            variant='ds-primary'
          >
            {isAllFilesFinishProcessed ? (
              isPendingDownloadAllFiles ? (
                <Spinner size='sm' />
              ) : (
                t('translator.downloadAll')
              )
            ) : (
              t('common.translate')
            )}
          </Button>
        </Box>
      )}
    </Flex>
  );
};

export default TranslatedFiles;
